import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { useNavigate, useParams } from 'react-router-dom'
import { ChatRoom } from '../../components/chat/ChatRoom'
import { ChatRoomErrorWrapper } from '../../components/chat/ChatRoomErrorWrapper'
import { RoomPane } from '../../components/chat/Room'
import { SideBar } from '../../components/common/SideBar'
import { ChatSideBarContent } from '../../components/chat/ChatSideBarContent'
import {
  ChatHeaderState,
  chatHeaderStateAtom,
} from '../../components/chat/atom'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../navigation/reverseDocumentTitle'
import { chatRoomQueries } from '../../api/chat'
import { useSelectedChatId } from '../../state/context/SelectedChatIdProvider'
import { reverseUrl } from '../../navigation/reverseUrl'
import { ConversationType } from '../../generated/api/chat-conversations'

export enum ActiveTabBar {
  RESIDENT = 0,
  BOARD = 1,
}

export enum ActiveTabResident {
  ONE_ON_ONE = 0,
  INTERESTS = 1,
}

export enum ActiveTabBoard {
  BOARD_CHAT = 0,
}

export const getConversationType = (
  activeTabBar: ActiveTabBar,
  activeTab: number
): ConversationType => {
  if (activeTabBar === ActiveTabBar.BOARD) return 'board'
  return activeTab === ActiveTabResident.INTERESTS ? 'interests' : 'oneonone'
}

export const ChatView = (): React.ReactNode => {
  const { id } = useParams()
  const { selectedChatRoomId, setSelectedChatRoomId } = useSelectedChatId()
  const [chatHeaderState, setChatHeaderState] = useAtom(chatHeaderStateAtom)
  useDocumentTitle(reverseDocumentTitle('chat'))

  const [activeTabBar, setActiveTabBar] = useState<ActiveTabBar>(
    ActiveTabBar.RESIDENT
  )
  const [activeTab, setActiveTab] = useState<
    ActiveTabResident | ActiveTabBoard
  >(ActiveTabResident.ONE_ON_ONE)
  const { data: header } = useQuery({
    ...chatRoomQueries.header(id ?? ''),
    enabled: !!id,
  })
  const roomType = header?.type
  const navigate = useNavigate()

  useEffect(() => {
    if (id) {
      setSelectedChatRoomId(id)
      return
    }
    if (!id && selectedChatRoomId) {
      navigate(reverseUrl('chat:detail', { id: selectedChatRoomId }))
    }
  }, [id, selectedChatRoomId, setSelectedChatRoomId, navigate])

  useEffect(() => {
    if (id) setChatHeaderState(ChatHeaderState.None)
  }, [id, setChatHeaderState])

  useEffect(() => {
    if (!roomType) return

    if (roomType === 'oneonone') {
      setActiveTabBar(ActiveTabBar.RESIDENT)
      setActiveTab(ActiveTabResident.ONE_ON_ONE)
      return
    }
    if (roomType === 'interests') {
      setActiveTabBar(ActiveTabBar.RESIDENT)
      setActiveTab(ActiveTabResident.INTERESTS)
      return
    }
    setActiveTabBar(ActiveTabBar.BOARD)
    setActiveTab(ActiveTabBoard.BOARD_CHAT)
  }, [roomType])

  const isNewChatMode =
    chatHeaderState === ChatHeaderState.StartNewChat ||
    chatHeaderState === ChatHeaderState.StartNewInterestsChat ||
    chatHeaderState === ChatHeaderState.StartNewBoardChat

  return (
    <>
      <SideBar className="gap-gutter flex" padding={false}>
        <ChatSideBarContent
          activeTabBar={activeTabBar}
          setActiveTab={setActiveTab}
          setActiveTabBar={setActiveTabBar}
          activeTab={activeTab}
        />
      </SideBar>
      <div className="flex flex-1">
        {id ? (
          <RoomPane roomId={id} />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col justify-between">
            <ChatRoomErrorWrapper
              activeTab={activeTab}
              activeTabBar={activeTabBar}
            >
              {isNewChatMode && <ChatRoom />}
            </ChatRoomErrorWrapper>
          </div>
        )}
      </div>
    </>
  )
}
